import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import axios from 'axios';
import { 
  Play, X, Youtube, BookOpen, Clock, ChevronRight, ChevronDown, ChevronUp,
  Video, ExternalLink, CheckCircle, Loader2, PictureInPicture2, Maximize2,
  Minimize2, Volume2, VolumeX, SkipForward, ThumbsUp, Wrench, Car, AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============== PICTURE-IN-PICTURE VIDEO PLAYER ==============

function PiPVideoPlayer({ video, isOpen, onClose, onWatched, autoPlay = true }) {
  const iframeRef = useRef(null);
  const containerRef = useRef(null);
  const [isPiP, setIsPiP] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [watchTime, setWatchTime] = useState(0);

  // Track watch time
  useEffect(() => {
    if (isOpen && loaded) {
      const interval = setInterval(() => {
        setWatchTime(prev => prev + 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isOpen, loaded]);

  // Mark as watched after 30 seconds
  useEffect(() => {
    if (watchTime >= 30 && video?.id) {
      onWatched?.(video.id);
    }
  }, [watchTime, video?.id, onWatched]);

  // Handle Picture-in-Picture
  const togglePiP = useCallback(async () => {
    if (!document.pictureInPictureEnabled) {
      alert('Picture-in-Picture is not supported in your browser');
      return;
    }

    try {
      if (isPiP) {
        await document.exitPictureInPicture();
        setIsPiP(false);
      } else {
        // For iframe, we minimize the container instead of true PiP
        setIsMinimized(true);
        setIsPiP(true);
      }
    } catch (err) {
      console.error('PiP error:', err);
      // Fallback to minimized mode
      setIsMinimized(true);
      setIsPiP(true);
    }
  }, [isPiP]);

  const handleClose = () => {
    if (video?.id && watchTime > 0) {
      // Log watch time to backend
      axios.post(`${API}/youtube-troubleshooting/mark-watched`, null, {
        params: { video_id: video.id, service_type: video.service_type || 'general', watched_seconds: watchTime }
      }).catch(() => {});
    }
    onWatched?.(video?.id);
    setIsPiP(false);
    setIsMinimized(false);
    setWatchTime(0);
    onClose();
  };

  if (!video || !isOpen) return null;

  // Minimized PiP-style view
  if (isMinimized) {
    return (
      <div
        ref={containerRef}
        className="fixed bottom-4 right-4 z-[9999] w-80 rounded-xl overflow-hidden shadow-2xl border border-gray-200 bg-black"
        style={{ aspectRatio: '16/9' }}
        data-testid="pip-video-minimized"
      >
        <iframe
          ref={iframeRef}
          src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0&modestbranding=1`}
          title={video.title}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
        <div className="absolute top-0 left-0 right-0 p-2 bg-gradient-to-b from-black/70 to-transparent flex items-center justify-between">
          <span className="text-white text-xs font-medium truncate max-w-[200px]">{video.title}</span>
          <div className="flex items-center gap-1">
            <Button 
              size="icon" 
              variant="ghost" 
              className="h-6 w-6 text-white hover:bg-white/20"
              onClick={() => setIsMinimized(false)}
            >
              <Maximize2 className="w-4 h-4" />
            </Button>
            <Button 
              size="icon" 
              variant="ghost" 
              className="h-6 w-6 text-white hover:bg-white/20"
              onClick={handleClose}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Full modal view
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden bg-gray-900" data-testid="pip-video-modal">
        <DialogHeader className="p-4 pb-2 bg-gradient-to-b from-gray-800 to-gray-900">
          <DialogTitle className="flex items-center gap-2 text-white text-lg pr-8">
            <Youtube className="w-5 h-5 text-red-500" />
            <span className="truncate">{video.title}</span>
          </DialogTitle>
          <p className="text-gray-400 text-sm">{video.channel}</p>
        </DialogHeader>
        
        <div className="relative w-full aspect-video bg-black">
          {!loaded && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900">
              <Loader2 className="w-10 h-10 text-red-500 animate-spin mb-2" />
              <span className="text-gray-400 text-sm">Loading video...</span>
            </div>
          )}
          <iframe
            ref={iframeRef}
            src={`https://www.youtube.com/embed/${video.id}?autoplay=${autoPlay ? 1 : 0}&rel=0&modestbranding=1`}
            title={video.title}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            onLoad={() => setLoaded(true)}
          />
        </div>

        <div className="p-4 bg-gray-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-4 text-sm text-gray-400">
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {video.duration}
            </span>
            <span className="flex items-center gap-1">
              <ThumbsUp className="w-4 h-4" />
              {video.rating} rating
            </span>
            <span>{video.views} views</span>
          </div>
          
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Button 
              variant="outline" 
              size="sm"
              className="flex-1 sm:flex-none border-gray-600 text-gray-300 hover:bg-gray-700"
              onClick={togglePiP}
            >
              <PictureInPicture2 className="w-4 h-4 mr-2" />
              Mini Player
            </Button>
            <a 
              href={video.watch_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-2 text-sm text-blue-400 hover:text-blue-300"
            >
              <ExternalLink className="w-4 h-4" />
              YouTube
            </a>
            <Button 
              onClick={handleClose}
              className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700 text-white"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Done
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ============== VIDEO THUMBNAIL CARD ==============

function VideoThumbnailCard({ video, onClick, isWatched, compact = false }) {
  return (
    <div 
      className={`
        relative group cursor-pointer rounded-xl overflow-hidden transition-all duration-300
        ${compact ? 'w-32 sm:w-40' : 'w-full'}
        ${isWatched ? 'ring-2 ring-green-500' : 'hover:ring-2 hover:ring-blue-400'}
        hover:shadow-xl hover:-translate-y-1
      `}
      onClick={() => onClick(video)}
      data-testid={`video-thumb-${video.id}`}
    >
      <div className="relative aspect-video bg-gray-200">
        <img 
          src={video.thumbnail}
          alt={video.title}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        {/* Play overlay */}
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center">
            <Play className="w-6 h-6 text-white" fill="white" />
          </div>
        </div>
        {/* Duration badge */}
        <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/80 rounded text-white text-xs font-medium">
          {video.duration}
        </div>
        {/* Watched indicator */}
        {isWatched && (
          <div className="absolute top-2 right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
            <CheckCircle className="w-4 h-4 text-white" />
          </div>
        )}
      </div>
      {!compact && (
        <div className="p-3 bg-white">
          <h4 className="font-medium text-gray-900 text-sm line-clamp-2 mb-1">{video.title}</h4>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span>{video.channel}</span>
            <span>•</span>
            <span>{video.views}</span>
          </div>
        </div>
      )}
    </div>
  );
}


// ============== CUSTOMER TROUBLESHOOTING MODAL ==============

export function CustomerTroubleshootingModal({ 
  isOpen, 
  onClose, 
  serviceType, 
  serviceName,
  onProceedToOrder 
}) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [watchedVideos, setWatchedVideos] = useState(() => {
    const saved = localStorage.getItem('jiffys_watched_troubleshoot');
    return saved ? JSON.parse(saved) : [];
  });
  const [showAllVideos, setShowAllVideos] = useState(false);

  useEffect(() => {
    if (isOpen && serviceType) {
      fetchVideos();
    }
  }, [isOpen, serviceType]);

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/youtube-troubleshooting/customer-preview/${serviceType}`);
      setVideos(response.data?.videos || []);
    } catch (error) {
      console.error('Failed to fetch troubleshooting videos:', error);
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoWatched = (videoId) => {
    if (videoId && !watchedVideos.includes(videoId)) {
      const updated = [...watchedVideos, videoId];
      setWatchedVideos(updated);
      localStorage.setItem('jiffys_watched_troubleshoot', JSON.stringify(updated));
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <Dialog open={isOpen && !selectedVideo} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="customer-troubleshoot-modal">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <Wrench className="w-6 h-6" />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold">
                  DIY {serviceName || serviceType}?
                </DialogTitle>
                <p className="text-blue-100 text-sm">
                  Watch these videos to see if you can fix it yourself
                </p>
              </div>
            </div>
          </div>

          <CardContent className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin mr-3" />
                <span className="text-gray-600">Loading helpful videos...</span>
              </div>
            ) : videos.length > 0 ? (
              <>
                {/* Video Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                  {(showAllVideos ? videos : videos.slice(0, 3)).map((video) => (
                    <VideoThumbnailCard
                      key={video.id}
                      video={video}
                      onClick={setSelectedVideo}
                      isWatched={watchedVideos.includes(video.id)}
                    />
                  ))}
                </div>

                {videos.length > 3 && !showAllVideos && (
                  <Button
                    variant="outline"
                    className="w-full mb-6"
                    onClick={() => setShowAllVideos(true)}
                  >
                    <ChevronDown className="w-4 h-4 mr-2" />
                    Show {videos.length - 3} More Videos
                  </Button>
                )}

                {/* Progress */}
                <div className="flex items-center justify-between text-sm text-gray-500 mb-6">
                  <span>
                    {watchedVideos.filter(id => videos.some(v => v.id === id)).length} of {videos.length} videos watched
                  </span>
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Videos help 70% of people fix it themselves!</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 h-12"
                    onClick={onClose}
                  >
                    <Video className="w-5 h-5 mr-2" />
                    I'll Try It Myself
                  </Button>
                  <Button
                    className="flex-1 h-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                    onClick={onProceedToOrder}
                  >
                    <Car className="w-5 h-5 mr-2" />
                    I Need Professional Help
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No videos available for this service</p>
                <Button
                  className="mt-4"
                  onClick={onProceedToOrder}
                >
                  Proceed to Order
                </Button>
              </div>
            )}
          </CardContent>
        </DialogContent>
      </Dialog>

      {/* Video Player */}
      <PiPVideoPlayer
        video={selectedVideo}
        isOpen={!!selectedVideo}
        onClose={() => setSelectedVideo(null)}
        onWatched={handleVideoWatched}
      />
    </>
  );
}


// ============== PROVIDER PREP VIDEOS COMPONENT ==============

export function ProviderPrepVideos({ 
  serviceType, 
  serviceName, 
  isVisible = true,
  onDismiss,
  autoExpand = true 
}) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [expanded, setExpanded] = useState(autoExpand);
  const [dismissed, setDismissed] = useState(false);
  const [watchedVideos, setWatchedVideos] = useState(() => {
    const saved = localStorage.getItem('jiffys_provider_watched');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    if (isVisible && serviceType) {
      fetchVideos();
    }
  }, [isVisible, serviceType]);

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/youtube-troubleshooting/provider-prep/${serviceType}`);
      setVideos(response.data?.videos || []);
    } catch (error) {
      console.error('Failed to fetch prep videos:', error);
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoWatched = (videoId) => {
    if (videoId && !watchedVideos.includes(videoId)) {
      const updated = [...watchedVideos, videoId];
      setWatchedVideos(updated);
      localStorage.setItem('jiffys_provider_watched', JSON.stringify(updated));
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  if (dismissed || !isVisible || videos.length === 0) return null;

  return (
    <>
      <Card 
        className="glass-primary border-blue-200 overflow-hidden mb-4"
        data-testid="provider-prep-videos"
      >
        {/* Header */}
        <div 
          className="p-4 bg-gradient-to-r from-blue-500 to-indigo-500 text-white cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                <BookOpen className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-sm sm:text-base">
                  Quick Review: {serviceName || serviceType}
                </h3>
                <p className="text-blue-100 text-xs sm:text-sm">
                  {loading ? 'Loading videos...' : `${videos.length} videos available`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 text-white hover:bg-white/20"
                onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
              >
                <X className="w-4 h-4" />
              </Button>
              {expanded ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        {expanded && (
          <CardContent className="p-4">
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
              </div>
            ) : (
              <>
                {/* Horizontal scroll on mobile */}
                <div className="flex gap-3 overflow-x-auto pb-3 -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-3 scrollbar-modern">
                  {videos.slice(0, 3).map((video) => (
                    <VideoThumbnailCard
                      key={video.id}
                      video={video}
                      onClick={setSelectedVideo}
                      isWatched={watchedVideos.includes(video.id)}
                      compact={true}
                    />
                  ))}
                </div>

                {/* Action buttons */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-blue-100">
                  <span className="text-xs text-gray-500">
                    {watchedVideos.filter(id => videos.some(v => v.id === id)).length}/{videos.length} watched
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-blue-600 border-blue-200"
                    onClick={handleDismiss}
                  >
                    <SkipForward className="w-4 h-4 mr-1" />
                    I'm Ready
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        )}
      </Card>

      {/* Video Player */}
      <PiPVideoPlayer
        video={selectedVideo}
        isOpen={!!selectedVideo}
        onClose={() => setSelectedVideo(null)}
        onWatched={handleVideoWatched}
      />
    </>
  );
}


// ============== FLOATING PIP VIDEO BUTTON ==============

export function FloatingVideoButton({ serviceType, serviceName }) {
  const [showVideos, setShowVideos] = useState(false);
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    if (serviceType) {
      axios.get(`${API}/youtube-troubleshooting/videos/${serviceType}`)
        .then(res => setVideos(res.data?.videos || []))
        .catch(() => setVideos([]));
    }
  }, [serviceType]);

  if (videos.length === 0) return null;

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setShowVideos(!showVideos)}
        className="fixed bottom-20 right-4 z-50 w-14 h-14 rounded-full bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg hover:shadow-xl transition-all hover:scale-105 flex items-center justify-center"
        data-testid="floating-video-btn"
      >
        <Youtube className="w-6 h-6" />
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 rounded-full text-xs flex items-center justify-center">
          {videos.length}
        </span>
      </button>

      {/* Video list popup */}
      {showVideos && (
        <div className="fixed bottom-36 right-4 z-50 w-72 bg-white rounded-xl shadow-2xl border overflow-hidden">
          <div className="p-3 bg-gradient-to-r from-red-500 to-red-600 text-white flex items-center justify-between">
            <span className="font-medium text-sm">Helpful Videos</span>
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6 text-white hover:bg-white/20"
              onClick={() => setShowVideos(false)}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {videos.map((video) => (
              <div
                key={video.id}
                onClick={() => { setSelectedVideo(video); setShowVideos(false); }}
                className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
              >
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-16 h-10 object-cover rounded"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-900 line-clamp-2">{video.title}</p>
                  <p className="text-xs text-gray-500">{video.duration}</p>
                </div>
                <Play className="w-4 h-4 text-gray-400" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Video player */}
      <PiPVideoPlayer
        video={selectedVideo}
        isOpen={!!selectedVideo}
        onClose={() => setSelectedVideo(null)}
        onWatched={() => {}}
      />
    </>
  );
}


// ============== EXPORTS ==============

export default PiPVideoPlayer;
export { PiPVideoPlayer, VideoThumbnailCard };
